<template>
  <div class="invoice-collections-history-container">
    <div v-if="origen === 'dashboard'" class="bank-details-board">
      <h3 class="history-title" style="color: white">DATOS BANCARIOS</h3>
      <div class="bank-details-inline">
        <h5 style="color: white; margin: 0">SANTANDER SPEI (CLABE):</h5>
        <h4 style="color: white; margin: 0">{{ bankDetails?.clabeBank }}</h4>
      </div>
      <div class="bank-details-inline">
        <h5 style="color: white; margin: 0">DATOS BANCARIOS SANTANDER CUENTA:</h5>
        <h4 style="color: white; margin: 0">{{ bankDetails?.accountNumberBank }}</h4>
      </div>
      <div class="bank-details-inline">
        <h5 style="color: white; margin: 0">BENEFICIARIO:</h5>
        <h4 style="color: white; margin: 0">{{ bankDetails?.beneficiaryName }}</h4>
      </div>
    </div>

    <div class="history-section">
      <h3 v-if="showTitle" class="history-title">Historial</h3>

      <div class="table-scroll">
        <table class="history-table">
          <thead class="table-header">
            <tr>
              <th>MES DE FACTURACIÓN</th>
              <th>FECHA LÍMITE DE PAGO</th>
              <th v-if="showAmountToPayColumn">MONTO A PAGAR</th>
              <th v-if="showConceptColumn">CONCEPTO</th>
              <th v-if="showProrrogaColumn">PRÓRROGA</th>
              <th>ESTADO DE CUENTA</th>
              <th v-if="hasAnyPreInvoice">PRE-FACTURA</th>
              <th
                v-if="
                  (hasAnyReceipt && showReceiptColumn) || (isDashboardView && showReceiptColumn)
                "
              >
                <div class="header-cell-content">
                  <span>{{ receiptColumnTitle }}</span>
                  <InfoButton
                    v-if="showReceiptInfoButton"
                    :messages="[
                      'Una vez se realice el pago, carga el comprobante para que se acredite en Mutuus',
                    ]"
                    type="info"
                  />
                </div>
              </th>
              <th v-if="showPaymentDateColumn">FECHA DE PAGO</th>
              <th v-if="hasAnyInvoice || (isDashboardView && showReceiptColumn)">FACTURA</th>
              <th v-if="hasAnyInvoiceXml || (isDashboardView && showReceiptColumn)">XML</th>
              <th v-if="showEditButton">EDITAR</th>
            </tr>
          </thead>

          <tbody>
            <!-- Loading State -->
            <tr v-if="isLoading">
              <td :colspan="getColumnCount()" class="loading-row">
                <div class="loading-text">Cargando historial...</div>
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-else-if="historyData.length === 0">
              <td :colspan="getColumnCount()" class="empty-state">
                <div class="empty-state-text">No existe información histórica</div>
              </td>
            </tr>

            <!-- Table Rows -->
            <tr v-else v-for="(item, index) in historyData" :key="index">
              <!-- MES DE FACTURACIÓN -->
              <td>{{ item.billingMonth }}</td>

              <!-- FECHA LÍMITE DE PAGO -->
              <td>
                <div v-if="item.paymentLimitDate">{{ item.paymentLimitDate }}</div>
                <div v-else class="empty-cell">
                  <span class="empty-text">No disponible</span>
                </div>
              </td>

              <!-- MONTO A PAGAR -->
              <td v-if="showAmountToPayColumn">
                <div v-if="normalizeAmount(item.amountToPay)">
                  ${{
                    parseFloat(item.amountToPay).toLocaleString('es-ES', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  }}
                </div>
                <div v-else class="empty-cell">
                  <span class="empty-text">No disponible</span>
                </div>
              </td>

              <!-- CONCEPTO -->
              <td v-if="showConceptColumn">
                <div v-if="item.concept && item.concept.trim() !== ''">{{ item.concept }}</div>
                <div v-else class="empty-cell">
                  <span class="empty-text">No disponible</span>
                </div>
              </td>

              <!-- PRÓRROGA -->
              <td v-if="showProrrogaColumn">
                <div v-if="item.paymentExtensionDate">{{ item.paymentExtensionDate }}</div>
                <div v-else class="empty-cell">
                  <span class="empty-text">No disponible</span>
                </div>
              </td>

              <!-- ESTADO DE CUENTA -->
              <td>
                <div
                  v-if="item.accountStatement && item.accountStatement.trim() !== ''"
                  class="document-actions"
                >
                  <span class="document-name"></span>
                  <button class="action-button" @click="downloadDocument(item.accountStatement)">
                    <img src="@/assets/download-icon.svg" alt="download" />
                  </button>
                </div>
                <div v-else class="empty-cell">
                  <span class="empty-text">No disponible</span>
                </div>
              </td>

              <!-- PRE-FACTURA -->
              <td v-if="hasAnyPreInvoice">
                <div
                  v-if="item.preInvoice && item.preInvoice.trim() !== ''"
                  class="document-actions"
                >
                  <span class="document-name"></span>
                  <button class="action-button" @click="downloadDocument(item.preInvoice)">
                    <img src="@/assets/download-icon.svg" alt="download" />
                  </button>
                </div>
                <div v-else class="empty-cell">
                  <span class="empty-text">No disponible</span>
                </div>
              </td>

              <!-- COMPROBANTE -->
              <td
                v-if="
                  (hasAnyReceipt && showReceiptColumn) || (isDashboardView && showReceiptColumn)
                "
              >
                <div v-if="isDashboardView" class="document-actions">
                  <span class="document-name"></span>
                  <div class="document-actions-inline">
                    <div v-if="item.receipt && item.receipt.trim() !== ''" class="receipt-buttons">
                      <button
                        class="action-button"
                        @click="updatePaymentProof(item.receiptId)"
                        aria-label="edit-receipt"
                      >
                        <img src="@/assets/pencil-icon.svg" alt="edit" />
                      </button>
                      <button
                        class="action-button"
                        @click="downloadDocument(item.receipt)"
                        aria-label="view-receipt"
                      >
                        <img src="@/assets/eye-black-icon.svg" alt="view" />
                      </button>
                    </div>
                    <button v-else class="action-button" @click="uploadPaymentProof(item)">
                      <img src="@/assets/upload-gray-icon.svg" alt="upload" />
                    </button>
                  </div>
                </div>
                <div v-else>
                  <div v-if="item.receipt && item.receipt.trim() !== ''" class="document-actions">
                    <span class="document-name"></span>
                    <button class="action-button" @click="downloadDocument(item.receipt)">
                      <img src="@/assets/download-icon.svg" alt="download" />
                    </button>
                  </div>
                  <div v-else class="empty-cell">
                    <span class="empty-text">No disponible</span>
                  </div>
                </div>
              </td>

              <!-- FECHA DE PAGO -->
              <td v-if="showPaymentDateColumn">
                <div v-if="item.paymentMadeDate">{{ item.paymentMadeDate }}</div>
                <div v-else class="empty-cell">
                  <span class="empty-text">No disponible</span>
                </div>
              </td>

              <!-- FACTURA -->
              <td v-if="hasAnyInvoice || (isDashboardView && showReceiptColumn)">
                <div v-if="item.invoice && item.invoice.trim() !== ''" class="document-actions">
                  <span class="document-name"></span>
                  <button class="action-button" @click="downloadDocument(item.invoice)">
                    <img src="@/assets/download-icon.svg" alt="download" />
                  </button>
                </div>
                <div v-else class="empty-cell">
                  <span class="empty-text">No disponible</span>
                </div>
              </td>

              <!-- FACTURA XML -->
              <td v-if="hasAnyInvoiceXml || (isDashboardView && showReceiptColumn)">
                <div
                  v-if="item.xmlInvoice && item.xmlInvoice.trim() !== ''"
                  class="document-actions"
                >
                  <button class="action-button" @click="downloadDocument(item.xmlInvoice)">
                    <img src="@/assets/download-icon.svg" alt="download" />
                  </button>
                </div>
                <div v-else class="empty-cell">
                  <span class="empty-text">No disponible</span>
                </div>
              </td>

              <!-- EDITAR -->
              <td v-if="showEditButton">
                <button class="edit-button" @click="editRow(item)">
                  <img src="@/assets/pencil-icon.svg" alt="edit" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Toast Message -->
      <ToastMessage
        v-model="showToast"
        :message="toastMessage"
        :submessage="toastSubmessage"
        :duration="10000"
        :type="toastType"
        :icon="toastIcon"
      />

      <!-- Edit Document Modal -->
      <BillingMonthModal
        :is-visible="showEditModal"
        :is-new-register="false"
        :document-type="editingDocumentType"
        :billing-month="editingBillingMonth"
        :billing-year="editingBillingYear"
        :current-payment-due-date="editingPaymentDueDate"
        :current-file-name="editingFileName"
        :editing-item-data="editingItemData"
        :current-account-statement-name="editingItemData?.accountStatement || ''"
        :current-pre-invoice-name="editingItemData?.preInvoice || ''"
        :current-invoice-name="editingItemData?.invoice || ''"
        :current-payment-proof-name="editingItemData?.receipt || ''"
        :current-payment-made-date="editingPaymentMadeDate"
        :current-xml-invoice-name="editingItemData?.xmlInvoice || ''"
        :is-loading="isEditLoading"
        @close="closeEditModal"
        @save="handleEditSave"
      />

      <!-- Loading for edit operations -->
      <CustomLoading :is-visible="isEditLoading" text="Procesando..." />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, onMounted } from 'vue'
import type { HistoryItem } from '@/utils/file-history'
import { ApiService } from '@/services/apiService'
import ToastMessage from './ToastMessage.vue'
import BillingMonthModal from './BillingMonthModal.vue'
import CustomLoading from './CustomLoading.vue'
import InfoButton from './InfoButton.vue'
import errorIcon from '@/assets/error-icon-white.svg'
import checkIcon from '@/assets/check-icon.svg'
import { FileTypes, StatusAccountPeriodDto } from 'commons'
import { useBulkLoadStore } from '@/stores/bulkLoad'
import { months, parseBillingMonth } from '@/utils/dateParsing'
import { validateFile } from '@/utils/fileValidation'
import { uploadFileToS3AndConfirmUpdate, uploadFileToS3AndConfirm } from '@/utils/fileUpload'
import { normalizeAmount } from '@/utils/amountUtils'
import type { BankDetailsDto } from 'commons'
import {
  ALLOWED_FILE_EXTENSIONS_ACCEPT,
  ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE,
  MAX_FILE_SIZE_BYTES,
} from '@/utils/file-constants'
import { trackGroupEvent } from '@/utils/googleAnalyticsEventsSender'

interface Props {
  historyData: HistoryItem[]
  showTitle?: boolean
  isLoading?: boolean
  showEditButton?: boolean
  showConceptColumn?: boolean
  showPaymentDateColumn?: boolean
  showReceiptColumn?: boolean
  receiptColumnTitle?: string
  showReceiptInfoButton?: boolean
  showAmountToPayColumn?: boolean
  showProrrogaColumn?: boolean
  isDashboardView?: boolean
  origen: string
}

const props = withDefaults(defineProps<Props>(), {
  showTitle: true,
  isLoading: false,
  showEditButton: false,
  showConceptColumn: false,
  showPaymentDateColumn: true,
  showReceiptColumn: true,
  receiptColumnTitle: 'COMPROBANTE',
  showReceiptInfoButton: false,
  showAmountToPayColumn: false,
  showProrrogaColumn: true,
  isDashboardView: false,
})

const emit = defineEmits<{
  'reload-data': []
  'upload-started': []
  'upload-finished': []
}>()

// Inject apiService
const apiService = inject<ApiService>('apiService')

if (!apiService) {
  throw new Error('apiService is not provided')
}

// Environment config service (unused but kept for future use)
// const envConfig = new WebEnvironmentConfigService()

// Toast message state
const showToast = ref(false)
const toastMessage = ref('')
const toastSubmessage = ref('')
const toastType = ref<'success' | 'error'>('success')
const toastIcon = ref<string | undefined>(undefined)

// Edit modal state
const showEditModal = ref(false)
const editingDocumentType = ref<'account-statement' | 'pre-invoice'>('account-statement')
const editingBillingMonth = ref('')
const editingBillingYear = ref('')
const editingPaymentDueDate = ref('')
const editingFileName = ref('')
const editingItemData = ref<HistoryItem | null>(null)
const editingPaymentMadeDate = ref('')

// Loading state for edit operations
const isEditLoading = ref(false)

// Check if any row has a pre-invoice
const hasAnyPreInvoice = computed(() => {
  return props.historyData.some((item) => item.preInvoice && item.preInvoice.trim() !== '')
})

const bankDetails = ref<BankDetailsDto | null>(null)

// Load feature flag on component mount
onMounted(async () => {
  await getBankDetails()
})
// Check if any row has a receipt
const hasAnyReceipt = computed(() => {
  return props.historyData.some((item) => item.receipt && item.receipt.trim() !== '')
})

// Check if any row has an invoice
const hasAnyInvoice = computed(() => {
  const hasInvoice = props.historyData.some((item) => item.invoice && item.invoice.trim() !== '')
  console.log('hasAnyInvoice check:', {
    historyData: props.historyData,
    hasInvoice,
    invoiceItems: props.historyData.filter((item) => item.invoice && item.invoice.trim() !== ''),
  })
  return hasInvoice
})

const hasAnyInvoiceXml = computed(() => {
  return props.historyData.some((item) => item.xmlInvoice && item.xmlInvoice.trim() !== '')
})

const uploadPaymentProof = async (item: HistoryItem) => {
  try {
    // Create a file input element
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = ALLOWED_FILE_EXTENSIONS_ACCEPT
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0]
      if (!file) return

      try {
        // Emit upload started event immediately when file is selected
        emit('upload-started')

        // Validate file
        validateFile(file, MAX_FILE_SIZE_BYTES, [...ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE])

        trackGroupEvent('upload_payment_proof', {
          location: 'Dashboard',
          company_name: useBulkLoadStore().selectedGroup?.nombrePublico,
        })

        // Get presigned URL for payment proof upload
        const response = await apiService.getSignedUrlForPaymentProof(
          item.id || '',
          file.name,
          file.type,
        )
        const uploadUrl = response.url

        // Upload file to S3 and confirm
        await uploadFileToS3AndConfirm(file, uploadUrl, response.statusAccountId, apiService)

        await apiService.sendVoucherSentEmail(useBulkLoadStore().selectedGroup.id)

        // Show success toast
        toastMessage.value = 'Éxito'
        toastSubmessage.value = 'Comprobante de pago subido correctamente'
        toastType.value = 'success'
        toastIcon.value = checkIcon
        showToast.value = true

        // Reload data to show the new file
        emit('reload-data')
      } catch (error) {
        console.error('Hubo un error: Error uploading payment proof ', error)

        // Show error toast with validation or upload error
        toastMessage.value = 'Error'
        toastSubmessage.value =
          error instanceof Error ? error.message : 'Error al subir el comprobante de pago'
        toastType.value = 'error'
        toastIcon.value = errorIcon
        showToast.value = true
      } finally {
        // Emit upload finished event
        emit('upload-finished')
      }
    }
    input.click()
  } catch (error) {
    console.error('Hubo un error: Error setting up payment proof upload ', error)
    toastMessage.value = 'Error'
    toastSubmessage.value = 'Error al configurar la subida del comprobante'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  }
}

const downloadDocument = async (filename: string) => {
  try {
    // Find the file in the history data to get the statusAccountId
    const fileItem = props.historyData.find(
      (item) =>
        item.accountStatement === filename ||
        item.preInvoice === filename ||
        item.receipt === filename ||
        item.invoice === filename ||
        item.xmlInvoice === filename,
    )

    if (!fileItem) {
      console.error('Hubo un error: File not found in history data ', filename)
      toastMessage.value = 'Error'
      toastSubmessage.value = 'Archivo no encontrado en el historial.'
      toastType.value = 'error'
      toastIcon.value = errorIcon
      showToast.value = true
      return
    }

    // Determine which file type and get the corresponding ID
    let statusAccountId = ''
    if (fileItem.accountStatement === filename) {
      statusAccountId = fileItem.accountStatementId || ''
    } else if (fileItem.preInvoice === filename) {
      statusAccountId = fileItem.preInvoiceId || ''
    } else if (fileItem.receipt === filename) {
      statusAccountId = fileItem.receiptId || ''
    } else if (fileItem.invoice === filename) {
      statusAccountId = fileItem.invoiceId || ''
    } else if (fileItem.xmlInvoice === filename) {
      statusAccountId = fileItem.xmlInvoiceId || ''
    }

    if (!statusAccountId) {
      console.error('Hubo un error: Status account ID not found for file ', filename)
      toastMessage.value = 'Error'
      toastSubmessage.value = 'ID del archivo no encontrado. Por favor, recarga la página.'
      toastType.value = 'error'
      toastIcon.value = errorIcon
      showToast.value = true
      return
    }

    console.log('Downloading file:', filename, 'with ID:', statusAccountId)

    // Get the presigned download URL
    const response = await apiService.getDownloadPresignedUrl(statusAccountId)
    const downloadUrl = response.url

    console.log('Download URL:', downloadUrl)

    // Check if it's a PDF file
    const isPdf = filename.toLowerCase().endsWith('.pdf')

    if (isPdf) {
      // Open PDF in new browser tab
      window.open(downloadUrl, '_blank')
    } else {
      // Download other file types
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    console.log('File download initiated successfully')
  } catch (error) {
    console.error('Hubo un error: Error downloading document ', error)

    // Show error toast message
    toastMessage.value = 'Error'
    toastSubmessage.value = 'Error al descargar el documento. Por favor, inténtalo de nuevo.'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  }
}

const updatePaymentProof = async (receiptId: string) => {
  // Implementation for updating payment proof if needed
  try {
    // Create a file input element
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = ALLOWED_FILE_EXTENSIONS_ACCEPT
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0]
      if (!file) return

      try {
        // Emit upload started event immediately when file is selected
        emit('upload-started')

        // Validate file
        validateFile(file, MAX_FILE_SIZE_BYTES, [...ALLOWED_FILE_EXTENSIONS_ARRAY_MUTABLE])

        // Get presigned URL for payment proof upload
        const response = await apiService.getUpdatePaymentProofUrl(receiptId, file.name, file.type)
        const uploadUrl = response.url

        await uploadFileToS3AndConfirmUpdate(file, uploadUrl, response.statusAccountId, apiService)

        await apiService.sendVoucherSentEmail(useBulkLoadStore().selectedGroup.id)

        // Show success toast
        toastMessage.value = 'Éxito'
        toastSubmessage.value = 'Comprobante de pago actualizado correctamente'
        toastType.value = 'success'
        toastIcon.value = checkIcon
        showToast.value = true

        // Reload data to show the new file
        emit('reload-data')
      } catch (error) {
        console.error('Hubo un error: Error uploading payment proof ', error)

        // Show error toast with validation or upload error
        toastMessage.value = 'Error'
        toastSubmessage.value =
          error instanceof Error ? error.message : 'Error al actualizar el comprobante de pago'
        toastType.value = 'error'
        toastIcon.value = errorIcon
        showToast.value = true
      } finally {
        // Emit upload finished event
        emit('upload-finished')
      }
    }
    input.click()
  } catch (error) {
    console.error('Hubo un error: Error setting up payment proof upload ', error)
    toastMessage.value = 'Error'
    toastSubmessage.value = 'Error al configurar la subida del comprobante'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  }
}

// Removed editDocument function as it's not used in this component

const editRow = (item: HistoryItem) => {
  // Extract month and year from billing month (format: "Septiembre 2025")
  const [month, year] = item.billingMonth.split(' ')
  editingBillingMonth.value = month
  editingBillingYear.value = year
  editingPaymentDueDate.value = item.paymentLimitDate
  editingFileName.value = item.accountStatement || item.preInvoice || ''
  editingDocumentType.value = 'account-statement'
  editingPaymentMadeDate.value = item.paymentMadeDate || ''

  // Store the complete item data for pre-loading
  editingItemData.value = { ...item }

  // Show modal
  showEditModal.value = true
}

const closeEditModal = () => {
  showEditModal.value = false
  editingDocumentType.value = 'account-statement'
  editingBillingMonth.value = ''
  editingBillingYear.value = ''
  editingPaymentDueDate.value = ''
  editingFileName.value = ''
  editingItemData.value = null
  editingPaymentMadeDate.value = ''
}

// Convert payment due date from dd/mm/yyyy to ISO 8601 format
const convertToISO8601 = (dateString: string): string => {
  const [day, month, year] = dateString.split('/')
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

const createStatusAccountPeriodDto = (
  data: Partial<StatusAccountPeriodDto>,
  existingItem: HistoryItem,
) => {
  const billingMonthString = `${editingBillingMonth.value} ${editingBillingYear.value}`
  const { month, year } = parseBillingMonth(billingMonthString)

  console.log('Month parsing debug:', {
    billingMonthString,
    editingBillingMonth: editingBillingMonth.value,
    editingBillingYear: editingBillingYear.value,
    parsedMonth: month,
    parsedYear: year,
  })

  const statusAccountPeriodDto = new StatusAccountPeriodDto()
  statusAccountPeriodDto.id = existingItem.id || ''
  statusAccountPeriodDto.groupId = useBulkLoadStore().selectedGroup.id
  statusAccountPeriodDto.month = month
  statusAccountPeriodDto.year = parseInt(year)
  statusAccountPeriodDto.concept = data.concept || ''
  statusAccountPeriodDto.invoiceNumber = data.invoiceNumber || ''
  statusAccountPeriodDto.paymentExtensionDate = data.paymentExtensionDate
    ? new Date(convertToISO8601(data.paymentExtensionDate))
    : undefined
  statusAccountPeriodDto.invoiceDate = new Date()
  statusAccountPeriodDto.amountToPay = parseFloat(data.amountToPay || '0')
  statusAccountPeriodDto.paymentMadeDate = data.paymentMadeDate
    ? new Date(convertToISO8601(data.paymentMadeDate))
    : undefined

  return { statusAccountPeriodDto, month, year }
}

const createStatusAccountFiles = (
  files: Array<{ file: File; fileType: FileTypes }>,
  existingItem: HistoryItem,
) => {
  return files.map((fileData) => {
    let existingFileId = ''
    if (fileData.fileType === FileTypes.ACCOUNT_STATEMENT && existingItem.accountStatementId) {
      existingFileId = existingItem.accountStatementId
    } else if (fileData.fileType === FileTypes.PRE_INVOICE && existingItem.preInvoiceId) {
      existingFileId = existingItem.preInvoiceId
    }

    return {
      id: existingFileId,
      fileName: fileData.file.name,
      fileType: fileData.fileType,
      contentType: fileData.file.type,
      loadState: '',
    }
  })
}

const uploadFiles = async (
  files: Array<{ file: File; fileType: FileTypes }>,
  response: Array<{ url: string; statusAccountId: string }>,
) => {
  for (let i = 0; i < files.length; i++) {
    const fileData = files[i]
    const uploadInfo = response[i]

    if (uploadInfo) {
      await uploadFileToS3AndConfirmUpdate(
        fileData.file,
        uploadInfo.url,
        uploadInfo.statusAccountId,
        apiService,
      )
      console.log(`File ${fileData.file.name} uploaded successfully`)
    }
  }
}

const sendBillingEmail = async (month: number, year: string, paymentDueDate?: string) => {
  try {
    const monthName = months[month - 1]
    await apiService.sendBillingAccessEmail(
      useBulkLoadStore().selectedGroup.id,
      monthName,
      year,
      paymentDueDate || '(No ingresada)',
      true,
    )
    console.log('Email de acceso a facturación enviado exitosamente')
  } catch (error) {
    console.error('Hubo un error: Error al enviar email de acceso a facturación ', error)
  }
}

const handleEditSave = async (
  data: Partial<StatusAccountPeriodDto> & { files?: Array<{ file: File; fileType: FileTypes }> },
) => {
  try {
    isEditLoading.value = true

    const existingItem = editingItemData.value
    if (!existingItem) {
      throw new Error('Existing document not found')
    }

    const { statusAccountPeriodDto, month, year } = createStatusAccountPeriodDto(data, existingItem)

    if (data.files) {
      statusAccountPeriodDto.statusAccountFiles = createStatusAccountFiles(data.files, existingItem)
    }

    console.log('Update statusAccountPeriodDto:', statusAccountPeriodDto)

    const response = await apiService.getUpdateUploadPresignedUrl(statusAccountPeriodDto)
    console.log('Update upload response:', response)

    if (data.files && data.files.length > 0) {
      await uploadFiles(data.files, response)
    }

    closeEditModal()

    toastMessage.value = 'Éxito'
    toastSubmessage.value = 'Documento actualizado correctamente'
    toastType.value = 'success'
    toastIcon.value = checkIcon
    showToast.value = true

    if (data.sendByEmail) {
      await sendBillingEmail(month, year, data.paymentDueDate)
    }

    emit('reload-data')
  } catch (error) {
    console.error('Hubo un error: Error saving document ', error)
    toastMessage.value = 'Error'
    toastSubmessage.value = 'Error al guardar el documento. Inténtalo de nuevo.'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  } finally {
    isEditLoading.value = false
  }
}

const getBankDetails = async () => {
  try {
    const response = await apiService.getBankDetails()
    bankDetails.value = response
  } catch (error) {
    console.error('Hubo un error: Error fetching bank details ', error)
    toastMessage.value = 'Error'
    toastSubmessage.value = 'No se pudieron obtener los datos bancarios.'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  }
}

const getColumnCount = () => {
  let count = 2 // MES DE FACTURACIÓN + FECHA LÍMITE DE PAGO
  if (props.showAmountToPayColumn) count++
  if (props.showConceptColumn) count++
  if (props.showProrrogaColumn) count++
  count++ // ESTADO DE CUENTA
  if (props.hasAnyPreInvoice) count++
  if (
    (props.hasAnyReceipt && props.showReceiptColumn) ||
    (props.isDashboardView && props.showReceiptColumn)
  )
    count++
  if (props.showPaymentDateColumn) count++
  if (props.hasAnyInvoice || (props.isDashboardView && props.showReceiptColumn)) count++
  if (props.hasAnyInvoiceXml || (props.isDashboardView && props.showReceiptColumn)) count++
  if (props.showEditButton) count++
  return count
}
</script>

<style scoped>
/* Root container */
.invoice-collections-history-container {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

/* History Section */
.history-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 40px;
  gap: 40px;
  width: 100%;
  max-width: 100%;
  background: #e9f0f6;
  border-radius: 24px;
  box-sizing: border-box;
  overflow: hidden;
}

/* Responsive padding adjustments for smaller screens */
@media (max-width: 1400px) {
  .history-section {
    padding: 32px;
    gap: 32px;
  }
}

@media (max-width: 1280px) {
  .history-section {
    padding: 24px;
    gap: 24px;
  }
}

@media (max-width: 768px) {
  .history-section {
    padding: 20px;
    gap: 20px;
  }
}

.history-title {
  width: 100%;
  height: 25px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 140%;
  color: #000000;
  text-align: left;
}

.table-scroll {
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}

/* Ensure table scroll works properly on smaller screens */
@media (max-width: 1600px) {
  .table-scroll {
    overflow-x: auto;
  }
}

.history-table {
  width: 100%;
  min-width: 1100px; /* Ajusta según la cantidad de columnas */
  border-collapse: collapse;
  table-layout: auto;
}

.history-table th,
.history-table td {
  padding: 10px 8px;
  text-align: left;
  white-space: nowrap;
}

.history-table thead {
  background: #003896;
  color: #fff;
}

.history-table thead th {
  color: #fafafa;
  text-align: center;
  margin: 0 auto;
}

/* EDITAR header - force narrow width */
.history-table thead th:last-child {
  width: 90px !important;
  min-width: 90px !important;
  max-width: 90px !important;
  padding: 0.5rem 0.5rem 0.5rem 0 !important; /* Add padding on right for centering (same as left padding of first column) */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
}

.history-table tbody tr {
  background: #ffffff;
  border-bottom: 1px solid #e0e0e0;
}

.history-table tbody tr:last-child {
  border-bottom: none;
}

.history-table tbody td {
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
  text-align: center;
  margin: 0 auto;
}

.header-cell-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
  text-align: center;
  word-wrap: break-word;
  white-space: normal;
  /* Inherit styles from parent th */
  color: inherit;
  font-weight: inherit;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

.header-cell-content span {
  /* Inherit all styles from parent th */
  color: inherit;
  font-weight: inherit;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

/* Column widths from Figma */
.history-table th:nth-child(1),
.history-table td:nth-child(1) {
  width: 150px; /* MES DE FACTURACIÓN */
}

.history-table th:nth-child(2),
.history-table td:nth-child(2) {
  width: 95px; /* FECHA LÍMITE DE PAGO */
}

.history-table th:nth-child(3),
.history-table td:nth-child(3) {
  width: 95px; /* PRÓRROGA */
}

.history-table th:nth-child(4),
.history-table td:nth-child(4) {
  width: 160px; /* ESTADO DE CUENTA */
}

.history-table th:nth-child(5),
.history-table td:nth-child(5) {
  width: 125px; /* PRE-FACTURA */
}

.history-table th:nth-child(6),
.history-table td:nth-child(6) {
  width: 150px; /* COMPROBANTE */
}

.history-table th:nth-child(7),
.history-table td:nth-child(7) {
  width: 115px; /* FECHA DE PAGO */
}

.history-table th:nth-child(8),
.history-table td:nth-child(8) {
  width: 95px; /* FACTURA */
}

.history-table th:nth-child(9),
.history-table td:nth-child(9) {
  width: 70px; /* XML */
}

/* EDITAR column - always the last column when visible */
.history-table th:last-child,
.history-table td:last-child {
  min-width: 110px; /* Asegura espacio para los íconos */
  text-align: center;
}

/* Fallback for nth-child(9) */
.history-table th:nth-child(9),
.history-table td:nth-child(9) {
  width: 70px !important;
  min-width: 70px !important;
  max-width: 70px !important;
  padding: 0.5rem 0.5rem 0.5rem 0 !important; /* Add padding on right for centering (same as left padding of first column) */
  box-sizing: border-box;
  text-align: center;
  overflow: hidden;
}

/* Pre-factura specific styling */
.data-cell:nth-child(4) .document-actions {
  width: 200px;
}

.data-cell:nth-child(4) .document-name {
  width: 91px;
}

/* Document Actions */
.document-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center; /* Center the content */
  padding: 0px;
  gap: 8px; /* Match Figma gap */
  margin: 0 auto;
  width: 100%;
  height: 32px;
  flex: none;
  order: 2;
  flex-grow: 0;
}

/* Reduce gap specifically for FACTURA column to match ESTADO DE CUENTA */
/* When PRÓRROGA column is visible (8th column) */
.data-cell:nth-child(8) .document-actions {
  gap: 4px; /* Very tight gap to match ESTADO DE CUENTA column spacing */
}

/* When PRÓRROGA column is hidden (7th column) - DashboardView scenario */
.data-cell:nth-child(7) .document-actions {
  gap: 4px; /* Very tight gap to match ESTADO DE CUENTA column spacing */
}

.document-name {
  width: auto;
  height: 22px;
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: #2b2a2a;
  flex: none;
  order: 0;
  flex-grow: 0;
  white-space: nowrap;
}

/* Specific spacing for other document columns to match ESTADO DE CUENTA */
.data-cell:nth-child(5) .document-name,
.data-cell:nth-child(6) .document-name {
  min-width: 100px;
}

.data-cell:nth-child(8) .document-name {
  min-width: 60px; /* FACTURA - shortest text needs less space */
}

/* When PRÓRROGA column is hidden (7th column) - DashboardView scenario */
.data-cell:nth-child(7) .document-name {
  min-width: 60px; /* FACTURA - shortest text needs less space */
}

.action-button {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  flex: none;
  flex-grow: 0;
  position: relative;
}

.action-button:hover {
  background: #f5f5f5;
}

.action-button img {
  width: 18px;
  height: 18px;
}

/* Inline wrapper for dashboard receipt buttons */
.document-actions .document-actions-inline {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center; /* Center the inline actions */
  gap: 8px;
  flex-wrap: nowrap;
}

.document-actions .receipt-buttons {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

/* Reduce spacing specifically for the COMPROBANTE column (6th data-cell) */
.data-cell:nth-child(6) .document-actions {
  gap: 6px; /* smaller gap between name and buttons */
  width: auto;
}

.data-cell:nth-child(6) .document-name {
  min-width: 80px; /* make the label narrower so buttons sit closer */
  white-space: nowrap;
}

.data-cell:nth-child(6) .document-actions .document-actions-inline {
  gap: 6px; /* slightly tighter between individual buttons */
}

/* Edit Button */
.edit-button {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  max-width: 32px !important;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  position: relative;
  margin: 0 auto;
  padding: 0 !important;
  vertical-align: middle;
}

.edit-button:hover {
  background: #f5f5f5;
}

.edit-button img {
  width: 18px !important;
  height: 18px !important;
  display: block;
  flex-shrink: 0;
}

.bank-details-board {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 24px;
  gap: 24px;
  width: 598px;
  max-width: 100%;
  height: 280px;
  background: #003896;
  border-radius: 24px;
  margin: 0 auto;
  /* Centrado horizontal */
  position: relative;
  margin-bottom: 25px;
  box-sizing: border-box;
}

/* Responsive bank details board */
@media (max-width: 768px) {
  .bank-details-board {
    width: 100%;
    padding: 20px;
  }
}

.bank-details-inline {
  display: flex;
  flex-direction: column;
  gap: 0;
}
</style>
